import axios from "axios";
import moment from "moment-timezone";

/**
 * A picked due date ("YYYY-MM-DD") sent as NOON LOCAL with its offset.
 * A bare date is parsed as UTC midnight, which is the previous evening in
 * the US, so a next action due "today" showed as a day overdue. Same fix
 * as the payment-date field (Session 88).
 */
export const toDueDate = (d?: string | null): string | null =>
  d ? moment(d, "YYYY-MM-DD").hour(12).minute(0).second(0).format() : null;

/**
 * Leads API client. docs/leads/LEADS_MODULE_SCOPE.md s.16.
 *
 * A lead is a client row in stage = lead. Everything here is company-side;
 * the client portal never sees these routes. Every call 403s until the
 * company setting LEADS_MODULE_ENABLED is on - `getLeadSettings()` is how
 * the nav finds out.
 */

export type LeadStatus =
  | "new"
  | "contacted"
  | "qualified"
  | "proposal_sent"
  | "negotiating"
  | "won"
  | "lost"
  | "dormant";

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  proposal_sent: "Proposal sent",
  negotiating: "Negotiating",
  won: "Won",
  lost: "Lost",
  dormant: "Dormant",
};

export interface LeadRow {
  id: string;
  name: string;
  contactName: string | null;
  contactEmail: string | null;
  phone: string | null;
  leadStatus: LeadStatus;
  leadSource: string | null;
  leadOwnerId: string | null;
  ownerName: string | null;
  nextActionAt: string | null;
  nextActionNote: string | null;
  createdAt: string;
  siteCount: number;
  rfqCount: number;
  quoteCount: number;
  /** Accepted quotes on this lead (online or by staff) - it needs converting. */
  acceptedQuoteCount?: number;
  proposalCount: number;
  unopenedProposals: number;
  lastActivityAt: string | null;
  lastActivitySummary: string | null;
  lastActivityKind: string | null;
  /** Set when the identity check pairs this lead with another lead or a client. */
  possibleDuplicateOf?: DuplicateFlag | null;
  /** Other leads at the same organization (different people). */
  sameOrg?: Array<{ id: string; name: string; contactName: string | null; kind: "client" | "lead" }>;
}

export interface DuplicateFlag {
  id: string;
  name: string;
  kind: "client" | "lead";
  reason: string;
}

export interface DuplicateMember {
  id: string;
  name: string;
  kind: "client" | "lead";
  leadStatus: string | null;
  ownerName: string | null;
  contactName: string | null;
  contactEmail: string | null;
  phone: string | null;
  website: string | null;
  nextActionAt: string | null;
  createdAt: string;
  rfqCount: number;
  quoteCount: number;
  proposalCount: number;
  siteCount: number;
  noteCount: number;
}

export interface DuplicateGroup {
  key: string;
  confidence: "exact" | "strong" | "probable";
  reasons: string[];
  members: DuplicateMember[];
}

export interface LeadStats {
  overdue: number;
  dueToday: number;
  unopenedProposals: number;
  open: number;
  /** Leads with an accepted quote, not yet converted. */
  awaitingConvert?: number;
}

export interface LeadInput {
  name: string;
  contactName?: string | null;
  contactTitle?: string | null;
  contactEmail?: string | null;
  phone?: string | null;
  website?: string | null;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  } | null;
  category?: string | null;
  leadStatus?: LeadStatus;
  leadSource?: string | null;
  leadOwnerId?: string | null;
  nextActionAt?: string | null;
  nextActionNote?: string | null;
  /** Set when the form was opened as a clone of another lead (a second person at the same org). */
  cloneOf?: string | null;
  lostReason?: string | null;
  leadMarkupPercent?: number | null;
}

export interface LeadActivityEvent {
  at: string;
  kind: string;
  text: string;
  refId: string | null;
  refType: "note" | "activity" | "proposal" | "rfq" | "quote" | null;
  actor: string | null;
}

/**
 * Shared identity check result (backend IdentityService). `id` is always
 * the ACCOUNT (top-level row); `contactId` is the person row that matched
 * when the hit came through a contact's email.
 */
export interface IdentityMatch {
  kind: "client" | "lead";
  id: string;
  name: string;
  contactId: string | null;
  confidence: "exact" | "strong" | "probable";
  matchedBy: "email" | "domain" | "website" | "name";
  /** Same organization AND the same person. Always true on an email hit. */
  samePerson: boolean;
  reason: string;
}

const fail = (e: any, what: string) => {
  const data = e?.response?.data;
  const detail = data?.message || data?.error;
  // A 409 from create carries the record it clashed with.
  const existing: IdentityMatch | undefined = data?.existing ?? undefined;
  if (Array.isArray(detail)) return { error: detail.join(", "), existing };
  if (detail) return { error: String(detail), existing };
  if (e?.response?.status) return { error: `${what} failed (${e.response.status}).` };
  return { error: `${what} failed.` };
};

/** Nav gate. Returns { enabled: false } on any failure so the link stays hidden. */
export const getLeadSettings = async (): Promise<LeadSettings> => {
  try {
    const res = await axios.get(`/leads/settings`);
    return { ...LEAD_SETTINGS_DEFAULTS, ...(res?.data ?? {}) };
  } catch {
    return { ...LEAD_SETTINGS_DEFAULTS };
  }
};

export interface LeadSettings {
  enabled: boolean;
  markupPercent: number;
  quoteLinkDays: number;
  followUpDays: number;
  defaultOwnerId: string | null;
  autoStatusOnSend: boolean;
  quoteLinkPassword: boolean;
}

export const LEAD_SETTINGS_DEFAULTS: LeadSettings = {
  enabled: false,
  markupPercent: 10,
  quoteLinkDays: 30,
  followUpDays: 3,
  defaultOwnerId: null,
  autoStatusOnSend: true,
  quoteLinkPassword: true,
};

/** The settings page: everything plus the owner list. Not behind the flag. */
export const getLeadSettingsFull = async (): Promise<{ settings: LeadSettings; owners: Array<{ id: string; name: string }> } | { error: string }> => {
  try {
    const res = await axios.get(`/leads/settings/full`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading lead settings");
  }
};

export const updateLeadSettings = async (input: Partial<LeadSettings>): Promise<LeadSettings | { error: string }> => {
  try {
    const res = await axios.put(`/leads/settings`, input);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Saving lead settings");
  }
};

/** Trigger the "leads due" digest now. `email` sends to one address only (testing). */
export const sendLeadDigest = async (opts: { force?: boolean; email?: string } = {}): Promise<{ sent: number; skipped: number; reason?: string } | { error: string }> => {
  try {
    const res = await axios.post(`/leads/digest/send`, null, {
      params: { force: opts.force ? "true" : undefined, email: opts.email || undefined, tz: new Date().getTimezoneOffset() },
    });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Sending the digest");
  }
};

/** TableData-shaped: { items, total, page, pages }. Sends the browser's tz offset so "due" is the user's day. */
export const getLeadsTable = async ({ query = {}, controller = null, excludeInterceptor = false }: any) => {
  try {
    const res: any = await axios.get(`/leads`, {
      params: { ...query, tz: new Date().getTimezoneOffset() },
      excludeInterceptor,
      ...(controller ? { signal: controller.signal } : {}),
    } as any);
    return res?.data ?? { items: [], total: 0, page: 1, pages: 0 };
  } catch {
    return { items: [], total: 0, page: 1, pages: 0 };
  }
};

export const getLeadStats = async (params: { owner?: string; source?: string } = {}): Promise<LeadStats | { error: string }> => {
  try {
    const res = await axios.get(`/leads/stats`, { params: { ...params, tz: new Date().getTimezoneOffset() } });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading lead counts");
  }
};

export const getLeadOptions = async (): Promise<{ owners: Array<{ id: string; name: string }>; sources: string[] }> => {
  try {
    const res = await axios.get(`/leads/options`);
    return res?.data ?? { owners: [], sources: [] };
  } catch {
    return { owners: [], sources: [] };
  }
};

export const createLead = async (input: LeadInput): Promise<any | { error: string; existing?: IdentityMatch }> => {
  try {
    const res = await axios.post(`/leads`, input);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Creating the lead");
  }
};

/** "Is this already someone?" - the same check create() refuses on. Null = nothing matches. */
export const checkLeadIdentity = async (input: { name?: string | null; contactEmail?: string | null; website?: string | null; contactName?: string | null; exclude?: string | null }): Promise<IdentityMatch | null> => {
  try {
    const res = await axios.post(`/leads/check`, input);
    return res?.data?.existing ?? null;
  } catch {
    return null;
  }
};

/** The enquiry becomes a note (+ activity) on an existing CLIENT instead of a new lead. */
export const noteOnExistingClient = async (clientId: string, text: string): Promise<{ clientId: string; name: string } | { error: string }> => {
  try {
    const res = await axios.post(`/leads/existing-client/${clientId}/note`, { text });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Adding the note");
  }
};

export const getLead = async (id: string): Promise<any | { error: string }> => {
  try {
    const res = await axios.get(`/leads/${id}`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading the lead");
  }
};

export const updateLead = async (id: string, input: Partial<LeadInput>): Promise<any | { error: string }> => {
  try {
    const res = await axios.put(`/leads/${id}`, input);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Saving the lead");
  }
};

export const getLeadActivity = async (id: string): Promise<{ items: LeadActivityEvent[] } | { error: string }> => {
  try {
    const res = await axios.get(`/leads/${id}/activity`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading activity");
  }
};

/** The composer: a note (with kind) and/or a next action, one request. */
export const logLeadActivity = async (
  id: string,
  input: { kind?: "note" | "call" | "email" | "meeting"; text?: string; nextActionAt?: string | null; nextActionNote?: string | null },
): Promise<any | { error: string }> => {
  try {
    const res = await axios.post(`/leads/${id}/activity`, input);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Saving activity");
  }
};

export const markLeadLost = async (id: string, reason: string | null): Promise<any | { error: string }> => {
  try {
    const res = await axios.post(`/leads/${id}/lost`, { reason });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Marking the lead lost");
  }
};

/** Soft delete. Refused (with the reason) when the lead has a project or invoice. */
export const deleteLead = async (id: string): Promise<{ deleted: true } | { error: string }> => {
  try {
    const res = await axios.delete(`/leads/${id}`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Deleting the lead");
  }
};

export type BulkLeadAction = "assign" | "status" | "next_action" | "lost" | "delete";

export const bulkLeads = async (
  ids: string[],
  action: BulkLeadAction,
  value?: any,
): Promise<{ done: number; failed: Array<{ id: string; name?: string; reason: string }> } | { error: string }> => {
  try {
    const res = await axios.post(`/leads/bulk`, { ids, action, value });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Updating the selected leads");
  }
};

export const getLeadLocations = async (id: string): Promise<{ items: any[]; total: number }> => {
  try {
    const res = await axios.get(`/leads/${id}/locations`);
    return res?.data ?? { items: [], total: 0 };
  } catch {
    return { items: [], total: 0 };
  }
};

/* ---- public quote links (s.5, s.16.3) ---------------------------------- */

/** New quote from a lead, step 1: skip the RFQ alert the estimate will trigger. Never blocks. */
export const beginLeadQuickQuote = async (leadId: string): Promise<void> => {
  try {
    await axios.post(`/leads/${leadId}/quick-quote/begin`);
  } catch {
    /* worst case: the normal RFQ alert goes out */
  }
};

/** Step 2: the quote exists - one "Quote ready for lead" alert. Never blocks. */
export const finishLeadQuickQuote = async (leadId: string, quoteId: string, serviceName: string | null): Promise<void> => {
  try {
    await axios.post(`/leads/${leadId}/quick-quote/done`, { quoteId, serviceName });
  } catch {
    /* the quote exists either way */
  }
};

export interface QuoteLink {
  id: string;
  token: string;
  url: string;
  password: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  viewCount: number;
  firstViewedAt: string | null;
  lastViewedAt: string | null;
  sentTo: string | null;
  acceptedAt: string | null;
  acceptedName: string | null;
  acceptedEmail: string | null;
  acceptedPoNumber: string | null;
  createdAt: string;
}

export const sendLeadQuote = async (
  leadId: string,
  quoteId: string,
  input: { to: string; cc?: string; bcc?: string; subject?: string; message?: string; requirePassword?: boolean; password?: string; includePassword?: boolean; skipEmail?: boolean },
): Promise<any | { error: string }> => {
  try {
    const res = await axios.post(`/leads/${leadId}/quotes/${quoteId}/send`, input);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Sending the quote");
  }
};

export const getLeadQuoteLink = async (leadId: string, quoteId: string): Promise<QuoteLink | null> => {
  try {
    const res = await axios.get(`/leads/${leadId}/quotes/${quoteId}/link`);
    return res?.data?.link ?? null;
  } catch {
    return null;
  }
};

export const extendLeadQuoteLink = async (leadId: string, quoteId: string, days = 30): Promise<any | { error: string }> => {
  try {
    const res = await axios.post(`/leads/${leadId}/quotes/${quoteId}/link/extend`, { days });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Extending the link");
  }
};

export const revokeLeadQuoteLink = async (leadId: string, quoteId: string): Promise<any | { error: string }> => {
  try {
    const res = await axios.post(`/leads/${leadId}/quotes/${quoteId}/link/revoke`, {});
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Revoking the link");
  }
};

/* ---- CSV import ------------------------------------------------------- */

export interface ImportRow {
  sourceRow: number;
  name: string;
  contactName: string | null;
  contactTitle: string | null;
  contactEmail: string | null;
  phone: string | null;
  website: string | null;
  address: { line1?: string; city?: string; state?: string; postalCode?: string } | null;
  leadSource: string | null;
  note: string | null;
  problem: string | null;
  /** Already in JSCRM - skipped on import. */
  existing?: { kind: "client" | "lead"; id: string; name: string; reason: string } | null;
}

export const parseLeadCsv = async (csv: string): Promise<{ rows: ImportRow[]; columns: Record<string, string | null>; headers: string[] } | { error: string }> => {
  try {
    const res = await axios.post(`/leads/import/parse`, { csv });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Reading the file");
  }
};

export const importLeads = async (
  rows: ImportRow[],
  ownerId: string | null,
  options: { leadStatus?: string | null; nextActionAt?: string | null; nextActionNote?: string | null } = {},
): Promise<{ created: Array<{ id: string; name: string }>; skipped: Array<{ sourceRow: number; name: string; reason: string }>; createdCount: number; skippedCount: number } | { error: string }> => {
  try {
    const res = await axios.post(`/leads/import`, { rows, ownerId, ...options });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Importing");
  }
};

/* ---- duplicates (LEADS_NEXT.md s.2) --------------------------------- */

/** Sent proposals on the other leads at this lead's organization, one per lineage. */
export interface SiblingProposal {
  id: string;
  version: number;
  name: string;
  prospectName: string;
  status: string;
  sentTo: string | null;
  sentAt: string | null;
  viewCount: number;
  clientId: string;
  leadName: string;
  contactName: string | null;
  lineageId: string;
  alreadyHere: boolean;
}

export const getSiblingProposals = async (leadId: string): Promise<SiblingProposal[]> => {
  try {
    const res = await axios.get(`/leads/${leadId}/sibling-proposals`);
    return res?.data?.items ?? [];
  } catch {
    return [];
  }
};

export const getLeadDuplicates = async (): Promise<{ groups: DuplicateGroup[]; total: number } | { error: string }> => {
  try {
    const res = await axios.get(`/leads/duplicates`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Loading duplicates");
  }
};

/** Merge `mergeIds` (leads) into the survivor (a lead or a client). */
export const mergeLeadDuplicates = async (
  survivorId: string,
  mergeIds: string[],
): Promise<{ survivorId: string; survivorKind: "client" | "lead"; merged: Array<{ id: string; name: string }> } | { error: string }> => {
  try {
    const res = await axios.post(`/leads/duplicates/merge`, { survivorId, mergeIds });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Merging");
  }
};

/** "Not duplicates" - remembered per pair. */
export const dismissLeadDuplicates = async (ids: string[]): Promise<{ dismissed: number } | { error: string }> => {
  try {
    const res = await axios.post(`/leads/duplicates/dismiss`, { ids });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Saving");
  }
};

/* ---- convert (s.16.4) --------------------------------------------------- */

export interface ConvertPreview {
  lead: { id: string; name: string; status: LeadStatus; contactName: string | null };
  /** Set when the organization is already a client: convert adds the contact to it instead of making a company. */
  existingClient: { id: string; name: string; reason: string } | null;
  /** Other leads at the same organization; they stay leads and join the client when they convert. */
  siblings: Array<{ id: string; name: string; contactName: string | null }>;
  invite: { firstName: string; lastName: string; email: string; possible: boolean };
  defaults: { terms: number | null; requiresDeposit: boolean; depositPercentage: number; portalLocationsEnabled: boolean; portalCalculatorEnabled: boolean };
}

export interface ConvertInput {
  invite?: boolean;
  inviteFirstName?: string;
  inviteLastName?: string;
  inviteEmail?: string;
  createZoho?: boolean;
  terms?: number | null;
  requiresDeposit?: boolean;
  depositPercentage?: number | null;
  portalLocationsEnabled?: boolean;
  portalCalculatorEnabled?: boolean;
}

export const getConvertPreview = async (leadId: string): Promise<ConvertPreview | { error: string }> => {
  try {
    const res = await axios.get(`/leads/${leadId}/convert`);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Preparing the conversion");
  }
};

export const convertLead = async (leadId: string, input: ConvertInput): Promise<any | { error: string }> => {
  try {
    const res = await axios.post(`/leads/${leadId}/convert`, input);
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    return fail(e, "Converting the lead");
  }
};

/* Find contacts (2026-10-15): fills blanks on the lead's own contact and
 * returns the deciders at the organization, best first. Can take ~30-60 s
 * when it has to research. */
export interface FoundContact {
  name: string;
  title: string | null;
  email: string | null;
  emailVerified: boolean;
  emailSource: string | null;
  phone: string | null;
  sourceUrl: string;
  /** 1 = procurement/facilities/operations ... 4 = executive, 5 = unknown */
  roleRank: number;
  existingLeadId: string | null;
}
export const findLeadContacts = async (
  id: string,
): Promise<{ filled: string[]; people: FoundContact[]; steps: Array<{ step: string; ran: boolean; ok: boolean; detail: string }>; costUsd: number; lead: any } | { error: string }> => {
  try {
    const res = await axios.post(`/leads/${id}/find-contacts`, {}, { timeout: 180000 });
    return res?.data ?? { error: "No data returned." };
  } catch (e: any) {
    // API Gateway gives up at ~29 s (503/504) while the server keeps going and
    // saves the result: tell the caller to wait for it, not to fail.
    const status = e?.response?.status;
    if ([502, 503, 504].includes(status) || !e?.response) return { pending: true } as any;
    return fail(e, "Finding contacts");
  }
};

/** The latest saved contact search on a lead, or null. */
export const getLeadContactSearch = async (id: string): Promise<any | null> => {
  try {
    const res = await axios.get(`/leads/${id}/contact-search`);
    return res?.data?.search ?? null;
  } catch {
    return null;
  }
};
